import { useAuth } from '@/context/AuthContext';
import { useHouseContext } from '@/context/HouseContext';
import {
	hasAnyPermission,
	hasPermission,
	type Permission,
	Permission as _Permmission,
	SCHEDULE_WRITE_PERMISSIONS,
} from '@/utils/permissionUtils';

/**
 * Returns the server-provided permissions for the current user in the selected house.
 * Reads from house.user_role.permissions which is populated by the API.
 */
export const usePermissions = (): readonly string[] => {
	const { selectedHouse } = useHouseContext();
	return selectedHouse?.user_role?.permissions ?? [];
};

/**
 * Checks a single permission against the server-provided set for the current house.
 */
export const useHasPermission = (permission: Permission): boolean => {
	const permissions = usePermissions();
	return hasPermission(permissions, permission);
};

/**
 * Checks whether the user has at least one of the given permissions.
 */
export const useHasAnyPermission = (required: readonly Permission[]): boolean => {
	const permissions = usePermissions();
	return hasAnyPermission(permissions, required);
};

/**
 * Named permission flags for the current user in the selected house.
 * Prefer this over multiple `useHasPermission` calls — one hook call, semantic names.
 */
export const useHousePermissions = () => {
	const permissions = usePermissions();
	const { isSuperAdmin } = useAuth();
	const has = (p: Permission) => hasPermission(permissions, p);
	return {
		canManageNetwork: has(_Permmission.HOUSE_NETWORK_MANAGE),
		/**
		 * Whether the property's service providers can be read at all. A viewer holds none of the
		 * network permissions, so `GET /houses/{id}/contacts` refuses them.
		 */
		canViewNetwork: has(_Permmission.HOUSE_NETWORK_VIEW) || isSuperAdmin,
		/**
		 * A super admin passes without a permission because the API grants them one: the houses list
		 * leaves `user_role` null for admins, so every permission here reads as absent for a house
		 * they are not a member of, while the endpoints behind these controls admit them anyway.
		 * Only this and `canViewNetwork` are bypassed — the other flags still hide their surfaces.
		 */
		canManageAccess: has(_Permmission.HOUSE_ACCESS_MANAGEMENT_MANAGE) || isSuperAdmin,
		canUploadDocuments: has(_Permmission.HOUSE_DOCUMENT_UPLOAD),
		canDeleteDocuments: has(_Permmission.HOUSE_DOCUMENT_DELETE),
		canDownloadDocuments: has(_Permmission.HOUSE_DOCUMENT_DOWNLOAD),
		canSetPrivateDocuments: has(_Permmission.HOUSE_DOCUMENT_PRIVATE_ALL),
		/**
		 * Whether any part of the plan can be changed, which is what decides if the schedule draws
		 * its write surfaces at all. Which verb a particular control needs is `planActionPermissions`.
		 */
		canWriteSchedule: hasAnyPermission(permissions, SCHEDULE_WRITE_PERMISSIONS),
		canCreateScheduleTask: has(_Permmission.HOUSE_SCHEDULE_CREATE),
		canAdmin: has(_Permmission.HOUSE_ADMIN),
		canRead: has(_Permmission.HOUSE_READ),
		/**
		 * Whether this reader is someone the property reports to, and so may be told about it.
		 *
		 * The same permission `MaintenanceNotificationSubscriber` filters recipients by, which is why
		 * copy promising a reminder is drawn behind it: a contributor records work on the plan without
		 * being who the property notifies, so telling them a reminder is coming is telling them wrong.
		 */
		canReceiveAlerts: has(_Permmission.ALERTS_MANAGE),
		canProposeDocument: has(_Permmission.HOUSE_DOCUMENT_SHARE_PROPOSED),
		canAcceptOrDenyDocument: has(_Permmission.HOUSE_DOCUMENT_SHARE_RESOLVE),
	};
};
